import styled from "@emotion/styled";
import type { FC } from "react";
import React, { useEffect, useRef, useState } from "react";
import { useBloc } from "src/state/state";
import "video.js/dist/video-js.css";
import type { VideoPlayerOptions } from "./FullVideoPlayerBloc";
import FullVideoPlayerBloc from "./FullVideoPlayerBloc";
import { Button } from "@9amhealth/shared";
import Translate from "src/ui/components/Translate/Translate";

const VideoFrame = styled.div`
  label: VideoFrame;
  overflow: hidden;
  width: 100%;
  height: 100%;
  position: relative;
  background: var(--color-charcoal);
  box-sizing: border-box;

  .video-js {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }

  .vjs-poster {
    img {
      object-fit: cover;
      width: 100%;
      height: 100%;
    }
  }
  &[data-native="true"] {
    width: 0;
    height: 0;
    opacity: 0;
  }
`;

export const ErrorOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 120;
  text-align: left;
  padding: 0.75rem 1rem;
  background: black;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const ErrorMessage = styled.p`
  &&& {
    color: white;
  }
`;

function createVideoEl(): HTMLVideoElement {
  const el = document.createElement("video");
  el.className = "video-js vjs-default-skin vjs-big-play-centered";
  el.id = "my-video";
  el.setAttribute("preload", "auto");
  return el;
}

const FullVideoPlayer: FC<{
  options?: VideoPlayerOptions;
  native?: boolean;
}> = ({ options }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [retrying, setRetrying] = useState(false);

  const [
    { errorMessage, providerErrorMessage },
    { initVideo, disposePlayer, reInitVideoFullVideo }
  ] = useBloc(FullVideoPlayerBloc, { create: () => new FullVideoPlayerBloc() });

  useEffect(() => {
    if (containerRef.current && options) {
      const el = createVideoEl();
      containerRef.current.appendChild(el);
      videoRef.current = el;
      void initVideo(el, options);
    }

    return () => {
      disposePlayer();
    };
  }, [options]);

  const handleRetry = async (): Promise<void> => {
    if (!containerRef.current || !options) {
      return;
    }
    setRetrying(true);
    try {
      disposePlayer();
      containerRef.current.innerHTML = "";

      const el = createVideoEl();
      containerRef.current.appendChild(el);
      videoRef.current = el;
      reInitVideoFullVideo(el, options, true);
    } catch (e) {
      console.error("retry failed", e);
    } finally {
      setRetrying(false);
    }
  };

  const showOverlay =
    retrying || Boolean(errorMessage) || Boolean(providerErrorMessage);

  return (
    <VideoFrame>
      <div ref={containerRef} />
      {showOverlay && (
        <ErrorOverlay role="alert" aria-live="assertive">
          <ErrorMessage>
            {providerErrorMessage} {errorMessage}
          </ErrorMessage>
          <Button
            theme="charcoal"
            onClick={() => {
              void handleRetry();
            }}
          >
            <Translate msg="retry" />
          </Button>
        </ErrorOverlay>
      )}
    </VideoFrame>
  );
};

export default FullVideoPlayer;
